/**
 * 背包面板（图标网格 + 悬浮提示）
 */

import type { PlayerState } from '../engine/Player';
import { getMaxInventorySize } from '../engine/Player';

export type PouchAction = 'use' | 'discard';

export interface PouchActionPayload {
  action: PouchAction;
  itemId: string;
}

export class PouchPanel {
  private container: HTMLElement;
  private onAction: ((payload: PouchActionPayload) => void) | null = null;
  private lastFingerprint = '';

  constructor(containerId: string) {
    const el = document.getElementById(containerId);
    if (!el) throw new Error(`Element #${containerId} not found`);
    this.container = el;
    this.container.className = 'panel';
  }

  setOnAction(callback: (payload: PouchActionPayload) => void): void {
    this.onAction = callback;
  }

  private fingerprint(player: PlayerState): string {
    const storageName = player.equipment['waist']?.name ?? 'none';
    const capacity = (player.equipment['waist'] as any)?.capacity ?? 8;
    const items = player.inventory.map((i) => i.id).sort().join(',');
    return `${storageName}|${items}|${player.inventory.length}`;
  }

  render(player: PlayerState): void {
    const fp = this.fingerprint(player);
    if (fp === this.lastFingerprint) return;
    this.lastFingerprint = fp;

    const maxSize = getMaxInventorySize(player);
    const storageEq = player.equipment['waist'] as any;
    const storageName = storageEq ? storageEq.name : '无';
    const count = player.inventory.length;
    const full = count >= maxSize;

    if (player.inventory.length === 0) {
      this.container.innerHTML = `
        <div class="panel-title">🎒 背包 <span class="pouch-cap">${storageName} ${count}/${maxSize}</span></div>
        <div class="pouch-empty">空空如也...</div>
      `;
      return;
    }

    let html = `<div class="panel-title">🎒 背包 <span class="pouch-cap ${full ? 'pouch-full' : ''}">${storageName} ${count}/${maxSize}</span></div>`;
    html += '<div class="pouch-grid">';

    for (const item of player.inventory) {
      const icon = this.itemIcon(item.type);
      const tooltip = this.itemTooltip(item);
      const useAttr = item.type === 'consumable' ? ` data-use="${item.id}"` : '';
      html += `
        <div class="pouch-cell" data-discard="${item.id}"${useAttr} title="${escapeHtml(tooltip)}">
          <span class="pouch-cell-icon">${icon}</span>
          ${item.type === 'consumable' ? '<span class="pouch-use-dot">●</span>' : ''}
        </div>
      `;
    }

    html += '</div>';
    this.container.innerHTML = html;

    // 点击消耗品 = 使用，右键 = 丢弃
    this.container.querySelectorAll('.pouch-cell').forEach((cell) => {
      const el = cell as HTMLElement;
      const discardId = el.dataset.discard;
      const useId = el.dataset.use;

      el.addEventListener('click', () => {
        if (useId && this.onAction) {
          this.onAction({ action: 'use', itemId: useId });
        }
      });

      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        if (discardId && this.onAction) {
          this.onAction({ action: 'discard', itemId: discardId });
        }
      });
    });
  }

  private itemIcon(type: string): string {
    switch (type) {
      case 'consumable': return '🧪';
      case 'material': return '🌿';
      case 'equipment': return '⚔️';
      default: return '📦';
    }
  }

  private itemTooltip(item: any): string {
    let tip = item.name;
    if (item.type === 'consumable') {
      const e: string[] = [];
      if (item.hpRestore) e.push(`回血${item.hpRestore}`);
      if (item.mpRestore) e.push(`回蓝${item.mpRestore}`);
      if (item.expBonus) e.push(`经验+${item.expBonus}`);
      if (e.length) tip += ` (${e.join('，')})`;
    }
    if (item.description) tip += `\n${item.description}`;
    tip += '\n左键使用 | 右键丢弃';
    return tip;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
